import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Search, Filter, UserCheck, UserX, Key, Mail, MessageSquare, RefreshCw, Users, UserPlus, Eye, EyeOff } from 'lucide-react';
import axios from 'axios';

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    user_type: '',
    city: '',
    is_active: '',
    date_from: '',
    date_to: ''
  });
  
  // Password reset state
  const [resetPassword, setResetPassword] = useState({
    userId: '',
    resetType: 'admin_generated',
    showDialog: false
  });
  const [generatedPassword, setGeneratedPassword] = useState('');
  
  // Notification state
  const [notification, setNotification] = useState({
    selectedUsers: [],
    title: '',
    message: '',
    type: 'info',
    showDialog: false
  });
  
  // Online users state
  const [onlineStats, setOnlineStats] = useState({
    total_online: 0,
    registered_online: 0,
    anonymous_online: 0
  });
  
  const { API, user } = useContext(AuthContext);
  
  useEffect(() => {
    fetchUsers();
    fetchOnlineStats();
    
    // Set up periodic refresh for online stats
    const interval = setInterval(fetchOnlineStats, 30000);
    
    return () => clearInterval(interval);
  }, []);
  
  useEffect(() => {
    handleSearch();
  }, [searchQuery, filters, users]);
  
  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/users`);
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Erro ao carregar usuários');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchOnlineStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/online-users`);
      setOnlineStats(response.data);
    } catch (error) {
      console.error('Error fetching online stats:', error);
    }
  };
  
  const handleSearch = async () => {
    if (!searchQuery && Object.values(filters).every(v => !v)) {
      setFilteredUsers(users);
      return;
    }
    
    try {
      const filterData = {
        search_query: searchQuery || undefined,
        user_type: filters.user_type || undefined,
        city: filters.city || undefined,
        is_active: filters.is_active !== '' ? filters.is_active === 'true' : undefined,
        date_from: filters.date_from || undefined,
        date_to: filters.date_to || undefined
      };
      
      const response = await axios.post(`${API}/admin/users/search`, filterData);
      setFilteredUsers(response.data);
    } catch (error) {
      console.error('Error searching users:', error);
      toast.error('Erro na busca de usuários');
      setFilteredUsers(users);
    }
  };
  
  const handleResetPassword = async (userId, resetType) => {
    try {
      const response = await axios.post(`${API}/admin/users/${userId}/reset-password`, {
        user_id: userId,
        reset_type: resetType
      });
      
      if (response.data.success) {
        if (resetType === 'admin_generated') {
          setGeneratedPassword(response.data.temporary_password);
          toast.success('Senha temporária gerada com sucesso!');
        } else {
          toast.success('Link de reset enviado!');
        }
      }
    } catch (error) {
      console.error('Error resetting password:', error);
      toast.error('Erro ao resetar senha');
    }
  };
  
  const toggleUserStatus = async (userId, currentStatus) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/status`, {
        is_active: !currentStatus
      });
      
      toast.success(`Usuário ${!currentStatus ? 'ativado' : 'desativado'} com sucesso!`);
      fetchUsers();
    } catch (error) {
      console.error('Error toggling user status:', error);
      toast.error('Erro ao alterar status do usuário');
    }
  };
  
  const sendNotifications = async () => {
    if (!notification.selectedUsers.length || !notification.title || !notification.message) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }
    
    try {
      const response = await axios.post(`${API}/admin/notifications`, {
        user_ids: notification.selectedUsers,
        title: notification.title,
        message: notification.message,
        type: notification.type
      });
      
      if (response.data.success) {
        toast.success(`Notificações enviadas para ${response.data.sent_count} usuários!`);
        setNotification({
          selectedUsers: [],
          title: '',
          message: '',
          type: 'info',
          showDialog: false
        });
      }
    } catch (error) {
      console.error('Error sending notifications:', error);
      toast.error('Erro ao enviar notificações');
    }
  };
  
  const getUserTypeColor = (type) => {
    switch (type) {
      case 'admin': return 'bg-red-100 text-red-800';
      case 'produtor': return 'bg-green-100 text-green-800';
      case 'consumidor': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };
  
  return (
    <div className="space-y-6">
      {/* Online Users Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Usuários Online</CardTitle>
            <Users className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{onlineStats.total_online}</div>
            <p className="text-xs text-muted-foreground">Total conectados agora</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Registrados Online</CardTitle>
            <UserCheck className="h-4 w-4 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">{onlineStats.registered_online}</div>
            <p className="text-xs text-muted-foreground">Usuários logados</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Visitantes</CardTitle>
            <Eye className="h-4 w-4 text-orange-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">{onlineStats.anonymous_online}</div>
            <p className="text-xs text-muted-foreground">Não registrados</p>
          </CardContent>
        </Card>
      </div>

      {/* Search and Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Search className="h-5 w-5" />
            Buscar e Filtrar Usuários
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2">
            <Input
              placeholder="Buscar por nome, email ou telefone..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="flex-1"
            />
            <button 
              onClick={handleSearch} 
              className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-100"
            >
              <Search className="h-4 w-4" />
            </button>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <select 
              value={filters.user_type} 
              onChange={(e) => setFilters(prev => ({...prev, user_type: e.target.value}))}
              className="p-2 border border-gray-300 rounded"
            >
              <option value="">Todos os tipos</option>
              <option value="consumidor">Consumidor</option>
              <option value="produtor">Produtor</option>
              <option value="admin">Administrador</option>
            </select>
            
            <Input
              placeholder="Cidade"
              value={filters.city}
              onChange={(e) => setFilters(prev => ({...prev, city: e.target.value}))}
            />
            
            <select 
              value={filters.is_active} 
              onChange={(e) => setFilters(prev => ({...prev, is_active: e.target.value}))}
              className="p-2 border border-gray-300 rounded"
            >
              <option value="">Todos</option>
              <option value="true">Ativo</option>
              <option value="false">Inativo</option>
            </select>
            
            <Input
              type="date"
              placeholder="Data inicial"
              value={filters.date_from}
              onChange={(e) => setFilters(prev => ({...prev, date_from: e.target.value}))}
            />
            
            <Input
              type="date"
              placeholder="Data final"
              value={filters.date_to}
              onChange={(e) => setFilters(prev => ({...prev, date_to: e.target.value}))}
            />
          </div>
          
          <div className="flex gap-2">
            <button 
              onClick={() => {
                setSearchQuery('');
                setFilters({user_type: '', city: '', is_active: '', date_from: '', date_to: ''});
              }} 
              className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-100"
            >
              Limpar Filtros
            </button>
            <button 
              onClick={fetchUsers} 
              className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-100 flex items-center gap-2"
            >
              <RefreshCw className="h-4 w-4" />
              Atualizar
            </button>
          </div>
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <div className="flex gap-2">
        <button 
          onClick={() => setNotification(prev => ({...prev, showDialog: true}))}
          className="px-4 py-2 bg-blue-600 text-white rounded flex items-center gap-2"
        >
          <MessageSquare className="h-4 w-4" />
          Enviar Notificações
        </button>
      </div>

      {/* Users Table */}
      <Card>
        <CardHeader>
          <CardTitle>
            Gerenciar Usuários ({filteredUsers.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-4">Carregando usuários...</div>
          ) : filteredUsers.length === 0 ? (
            <div className="text-center py-4 text-gray-500">
              Nenhum usuário encontrado
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left p-2">
                      <input
                        type="checkbox"
                        onChange={(e) => {
                          if (e.target.checked) {
                            setNotification(prev => ({...prev, selectedUsers: filteredUsers.map(u => u.id)}));
                          } else {
                            setNotification(prev => ({...prev, selectedUsers: []}));
                          }
                        }}
                        checked={notification.selectedUsers.length === filteredUsers.length && filteredUsers.length > 0}
                      />
                    </th>
                    <th className="text-left p-2">Nome</th>
                    <th className="text-left p-2">Email</th>
                    <th className="text-left p-2">Telefone</th>
                    <th className="text-left p-2">Cidade</th>
                    <th className="text-left p-2">Tipo</th>
                    <th className="text-left p-2">Status</th>
                    <th className="text-left p-2">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((user) => (
                    <tr key={user.id} className="border-b hover:bg-gray-50">
                      <td className="p-2">
                        <input
                          type="checkbox"
                          checked={notification.selectedUsers.includes(user.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setNotification(prev => ({...prev, selectedUsers: [...prev.selectedUsers, user.id]}));
                            } else {
                              setNotification(prev => ({...prev, selectedUsers: prev.selectedUsers.filter(id => id !== user.id)}));
                            }
                          }}
                        />
                      </td>
                      <td className="p-2 font-medium">{user.name}</td>
                      <td className="p-2">{user.email}</td>
                      <td className="p-2">{user.phone}</td>
                      <td className="p-2">{user.city}</td>
                      <td className="p-2">
                        <Badge className={getUserTypeColor(user.user_type)}>
                          {user.user_type}
                        </Badge>
                      </td>
                      <td className="p-2">
                        <Badge variant={user.is_active ? "default" : "secondary"}>
                          {user.is_active ? 'Ativo' : 'Inativo'}
                        </Badge>
                      </td>
                      <td className="p-2">
                        <div className="flex gap-1">
                          <button
                            onClick={() => setResetPassword({
                              userId: user.id,
                              resetType: 'admin_generated',
                              showDialog: true
                            })}
                            title="Resetar Senha"
                            className="p-1 border border-gray-300 rounded hover:bg-gray-100"
                          >
                            <Key className="h-3 w-3" />
                          </button>
                          
                          <button
                            onClick={() => toggleUserStatus(user.id, user.is_active)}
                            title={user.is_active ? 'Desativar' : 'Ativar'}
                            className="p-1 border border-gray-300 rounded hover:bg-gray-100"
                          >
                            {user.is_active ? <UserX className="h-3 w-3" /> : <UserCheck className="h-3 w-3" />}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Notification Dialog */}
      {notification.showDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full">
            <h3 className="text-lg font-semibold mb-4">Enviar Notificações para Usuários</h3>
            <div className="space-y-4">
              <div>
                <Label>Usuários Selecionados ({notification.selectedUsers.length})</Label>
                <div className="text-sm text-gray-600 max-h-20 overflow-y-auto">
                  {notification.selectedUsers.length === 0 ? 'Nenhum usuário selecionado' : 
                   `${notification.selectedUsers.length} usuário(s) selecionado(s)`}
                </div>
              </div>
              
              <div>
                <Label>Tipo de Notificação</Label>
                <select 
                  value={notification.type} 
                  onChange={(e) => setNotification(prev => ({...prev, type: e.target.value}))}
                  className="w-full p-2 border border-gray-300 rounded"
                >
                  <option value="info">Informação</option>
                  <option value="success">Sucesso</option>
                  <option value="warning">Aviso</option>
                  <option value="error">Erro</option>
                </select>
              </div>
              
              <div>
                <Label>Título *</Label>
                <Input
                  value={notification.title}
                  onChange={(e) => setNotification(prev => ({...prev, title: e.target.value}))}
                  placeholder="Título da notificação"
                />
              </div>
              
              <div>
                <Label>Mensagem *</Label>
                <textarea
                  value={notification.message}
                  onChange={(e) => setNotification(prev => ({...prev, message: e.target.value}))}
                  placeholder="Conteúdo da notificação"
                  rows={4}
                  className="w-full p-2 border border-gray-300 rounded"
                />
              </div>
              
              <div className="flex gap-2">
                <button 
                  onClick={sendNotifications} 
                  disabled={!notification.selectedUsers.length || !notification.title || !notification.message}
                  className="px-4 py-2 bg-blue-600 text-white rounded disabled:bg-gray-400"
                >
                  Enviar Notificações
                </button>
                <button 
                  onClick={() => setNotification(prev => ({...prev, showDialog: false}))}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Password Reset Dialog */}
      {resetPassword.showDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full">
            <h3 className="text-lg font-semibold mb-4">Resetar Senha do Usuário</h3>
            <div className="space-y-4">
              <div>
                <Label>Tipo de Reset</Label>
                <select 
                  value={resetPassword.resetType} 
                  onChange={(e) => setResetPassword(prev => ({...prev, resetType: e.target.value}))}
                  className="w-full p-2 border border-gray-300 rounded"
                >
                  <option value="admin_generated">Gerar Senha Temporária</option>
                  <option value="email_link">Enviar Link por Email</option>
                </select>
              </div>
              
              {generatedPassword && (
                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded">
                  <Label className="text-yellow-800">Senha Temporária Gerada:</Label>
                  <div className="font-mono text-lg font-bold text-yellow-900 mt-1">
                    {generatedPassword}
                  </div>
                  <p className="text-sm text-yellow-700 mt-2">
                    Copie esta senha e envie para o usuário. Ela deve ser alterada no próximo login.
                  </p>
                </div>
              )}
              
              <div className="flex gap-2">
                <button 
                  onClick={() => {
                    handleResetPassword(resetPassword.userId, resetPassword.resetType);
                    if (resetPassword.resetType === 'email_link') {
                      setResetPassword(prev => ({...prev, showDialog: false}));
                    }
                  }}
                  className="px-4 py-2 bg-blue-600 text-white rounded"
                >
                  {resetPassword.resetType === 'admin_generated' ? 'Gerar Senha' : 'Enviar Link'}
                </button>
                <button 
                  onClick={() => {
                    setResetPassword({userId: '', resetType: 'admin_generated', showDialog: false});
                    setGeneratedPassword('');
                  }}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded"
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;